import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Download, FileText, FileCode, Copy, Check, AlertCircle, Lightbulb, HelpCircle, Eye, EyeOff, Sparkles, X, ArrowRight, Loader2, TreeDeciduous } from 'lucide-react';
import { AppSelect } from '../../components/ui/AppSelect';
import { useNavigate } from 'react-router-dom';
import { validateDriver } from './validation';
import { exportDriver } from './export';
import { SentenceClassifier } from './SentenceClassifier';
import { InstructionPanel } from './InstructionPanel';
import { currentStateExamples, problemOpportunityExamples, ramificationsExamples, type Example } from './examples';
import type { BucketType } from './classification';
import { useOutcomeDriverStore } from '../../stores/prioritization/outcomeDriverStore';
import { useStrategyStore } from '../../stores/prioritization/strategyStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { StrategyConnectivityHelpButton } from '../../pages/prioritization/strategy/StrategyConnectivityDocModal';

interface QuickDriverModeProps {
  onBack: () => void;
  cycleId?: string | null;
  cycleName?: string | null;
  returnUrl?: string | null;
}

interface DriverData {
  contextOfPurpose: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
}

export const QuickDriverMode: React.FC<QuickDriverModeProps> = ({ 
  onBack,
  cycleId,
  cycleName,
  returnUrl
}) => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState<DriverData>({
    contextOfPurpose: '',
    currentState: '',
    problemOpportunity: '',
    ramifications: '',
    narrative: ''
  });

  const createDriver = useOutcomeDriverStore((s) => s.createDriver);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const strategyMap     = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const strategies      = useMemo(
    () => Object.values(strategyMap).map((s) => ({ id: s.id, name: s.name })),
    [strategyMap],
  );

  const [strategyId, setStrategyId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [showComponents, setShowComponents] = useState(true);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showClassifier, setShowClassifier] = useState(false);
  const [classifierInitialText, setClassifierInitialText] = useState('');
  const [showExamples, setShowExamples] = useState<{ bucket: BucketType | null }>({ bucket: null });
  const [selectedText, setSelectedText] = useState('');
  const narrativeRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies, currentWorkspaceId]);

  // Auto-generate suggested narrative when components change
  useEffect(() => {
    if (driver.currentState && driver.problemOpportunity && driver.ramifications) {
      const suggested = `${driver.currentState}. ${driver.problemOpportunity}. ${driver.ramifications}.`;
      // Only update if narrative is empty or matches previous suggestion
      if (!driver.narrative || driver.narrative === driver.currentState + '. ' + driver.problemOpportunity + '. ' + driver.ramifications + '.') {
        setDriver(prev => ({ ...prev, narrative: suggested }));
      }
    }
  }, [driver.currentState, driver.problemOpportunity, driver.ramifications]);

  const handleGenerateSuggested = () => {
    if (driver.currentState && driver.problemOpportunity && driver.ramifications) {
      const suggested = `${driver.currentState}. ${driver.problemOpportunity}. ${driver.ramifications}.`;
      setDriver(prev => ({ ...prev, narrative: suggested }));
    }
  };

  const handleReview = () => {
    const results = validateDriver(driver);
    setValidationResults(results);
    setShowValidation(true);
  };

  const handleSave = async (andReturn: boolean = false) => {
    if (!driver.narrative?.trim()) {
      alert('Please complete the driver narrative before saving.');
      return;
    }

    setIsSaving(true);
    const title = driver.contextOfPurpose?.substring(0, 80).trim() || driver.narrative.substring(0, 50) || 'Quick Driver';
    const { driver: saved, error } = await createDriver({
      title,
      contextOfPurpose: driver.contextOfPurpose,
      currentState: driver.currentState,
      problemOpportunity: driver.problemOpportunity,
      ramifications: driver.ramifications,
      narrative: driver.narrative,
      cycleId: cycleId ?? null,
      strategyId: strategyId || null,
    });
    setIsSaving(false);

    if (error || !saved) {
      alert(`Failed to save driver: ${error ?? 'Unknown error'}`);
      return;
    }

    if (returnUrl) {
      navigate(returnUrl, {
        state: {
          returnedDrivers: [{ id: saved.id, title: saved.title, narrative: saved.narrative }],
          fromGenerator: true,
          forCycleId: cycleId ?? undefined,
        },
      });
    } else {
      alert('Driver saved!');
    }
  };

  const handleExport = (format: 'markdown' | 'word' | 'pdf') => {
    exportDriver(driver, format);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(driver.narrative);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenClassifier = (initialText?: string, bucket?: BucketType) => {
    setClassifierInitialText(initialText || '');
    setShowClassifier(true);
  };

  const handleInsertFromClassifier = (sentence: string, bucket: BucketType) => {
    if (bucket === 'current-state') {
      setDriver(prev => ({ ...prev, currentState: prev.currentState ? `${prev.currentState} ${sentence}` : sentence }));
    } else if (bucket === 'problem-opportunity') {
      setDriver(prev => ({ ...prev, problemOpportunity: prev.problemOpportunity ? `${prev.problemOpportunity} ${sentence}` : sentence }));
    } else if (bucket === 'ramifications') {
      setDriver(prev => ({ ...prev, ramifications: prev.ramifications ? `${prev.ramifications} ${sentence}` : sentence }));
    }
  };

  const handleTextSelection = () => {
    const textarea = narrativeRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      if (start !== end) {
        const selected = driver.narrative.substring(start, end);
        setSelectedText(selected);
      } else {
        setSelectedText('');
      }
    }
  };

  const getBucketTooltip = (bucket: 'current-state' | 'problem-opportunity' | 'ramifications'): string => {
    switch (bucket) {
      case 'current-state':
        return 'What is true? List concrete facts or observations. No interpretations. No judgments. No consequences.';
      case 'problem-opportunity':
        return 'What does the truth mean? Describe the underlying issue, friction, or opportunity created by the facts.';
      case 'ramifications':
        return 'Why does this matter? State the consequences of staying on the current path.';
    }
  };

  const getExamplesForBucket = (bucket: BucketType): Example[] => {
    switch (bucket) {
      case 'current-state':
        return currentStateExamples;
      case 'problem-opportunity':
        return problemOpportunityExamples;
      case 'ramifications':
        return ramificationsExamples;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Outcome Drivers
        </button>
        <h1 className="text-2xl font-bold text-charcoal-900 mb-1">New Outcome Driver</h1>
        <p className="text-charcoal-700 text-sm">
          Articulate why change is needed — context, current state, problem/opportunity, and ramifications.
          {returnUrl && cycleName && (
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] ml-2">• For cycle: {cycleName}</span>
          )}
        </p>
        </div>
        <div className="sm:pt-1 flex-shrink-0">
          <StrategyConnectivityHelpButton context="outcome_driver_new" />
        </div>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6 items-start">

        {/* ── Left: form fields ────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-6">

        <div>
          {/* Step 1: Context of Purpose */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold">1</div>
              <h2 className="text-xl font-bold text-charcoal-900">Context of Purpose</h2>
            </div>
            <p className="text-charcoal-700 mb-4 text-sm">
              What area of your work or life is this about? (e.g., your team's priorities, your role, your product, your schedule)
            </p>
            <textarea
              value={driver.contextOfPurpose}
              onChange={(e) => setDriver(prev => ({ ...prev, contextOfPurpose: e.target.value }))}
              placeholder="Enter 1-3 sentences describing the context..."
              className="w-full min-h-[100px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {!driver.contextOfPurpose && (
              <p className="text-[var(--mustard)] text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Optional but strongly encouraged—feedback quality will be lower without context
              </p>
            )}
          </div>

          {/* Step 2: Component Fields */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold">2</div>
                <h2 className="text-xl font-bold text-charcoal-900">Driver Components</h2>
              </div>
              <button
                onClick={() => setShowComponents(!showComponents)}
                className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]"
              >
                {showComponents ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showComponents ? 'Hide' : 'Show'} Components
              </button>
            </div>

            {showComponents && (
              <div className="space-y-6">
                {/* Current State */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Current State</h3>
                    <button
                      type="button"
                      className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                      title={getBucketTooltip('current-state')}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Describe what's observably true right now. Stick to facts and objective conditions — things a camera could capture.
                  </p>
                  <textarea
                    value={driver.currentState}
                    onChange={(e) => setDriver(prev => ({ ...prev, currentState: e.target.value }))}
                    placeholder="Enter a factual description of the current state..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(driver.currentState, 'current-state')}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowExamples({ bucket: 'current-state' })}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      See an example
                    </button>
                  </div>
                </div>

                {/* Problem / Opportunity */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Problem / Opportunity</h3>
                    <button
                      type="button"
                      className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                      title={getBucketTooltip('problem-opportunity')}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Explain the core issue created by the current state. What do the facts mean? What's the real tension or mismatch?
                  </p>
                  <textarea
                    value={driver.problemOpportunity}
                    onChange={(e) => setDriver(prev => ({ ...prev, problemOpportunity: e.target.value }))}
                    placeholder="Enter the core problem or opportunity..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(driver.problemOpportunity, 'problem-opportunity')}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowExamples({ bucket: 'problem-opportunity' })}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      See an example
                    </button>
                  </div>
                </div>

                {/* Ramifications / Value Judgment */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Ramifications / Value Judgment</h3>
                    <button
                      type="button"
                      className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                      title={getBucketTooltip('ramifications')}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Make the stakes clear. What happens if nothing changes? Why does this matter enough to act now?
                  </p>
                  <textarea
                    value={driver.ramifications}
                    onChange={(e) => setDriver(prev => ({ ...prev, ramifications: e.target.value }))}
                    placeholder="Enter why this matters and what's at stake..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(driver.ramifications, 'ramifications')}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowExamples({ bucket: 'ramifications' })}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      See an example
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Composite Narrative */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold">3</div>
                <h2 className="text-xl font-bold text-charcoal-900">Composite Narrative</h2>
              </div>
              <button
                onClick={handleGenerateSuggested}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-2"
              >
                <Lightbulb className="h-4 w-4" />
                Update Suggested Draft
              </button>
            </div>
            <p className="text-sm text-[var(--text-sec)] mb-4">
              This is your complete Outcome Driver narrative. Edit it directly to create a flowing paragraph or two.
            </p>
            <textarea
              ref={narrativeRef}
              value={driver.narrative}
              onChange={(e) => setDriver(prev => ({ ...prev, narrative: e.target.value }))}
              onSelect={handleTextSelection}
              placeholder="Your complete Outcome Driver narrative will appear here..."
              className="w-full min-h-[200px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)]"
            />
            {selectedText && (
              <div className="mt-2">
                <button
                  onClick={() => handleOpenClassifier(selectedText)}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                >
                  <Sparkles className="h-3 w-3" />
                  Classify Selected Text
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {/* Primary save action */}
            {returnUrl ? (
              <button
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white px-4 py-2 font-medium text-sm transition-colors"
                title={`Save and return to ${cycleName || 'cycle'}`}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Save & Return to Cycle
              </button>
            ) : (
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white px-4 py-2 font-medium text-sm transition-colors"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Driver
              </button>
            )}

            {/* Secondary: review */}
            <button
              onClick={handleReview}
              className="flex items-center gap-2 border border-[var(--rule)] bg-transparent hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)] px-4 py-2 font-medium text-sm transition-colors"
            >
              <Eye className="h-4 w-4" />
              Review
            </button>

            {/* Secondary: save draft (cycle context only) */}
            {returnUrl && (
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="flex items-center gap-2 border border-[var(--rule)] bg-transparent hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)] px-4 py-2 font-medium text-sm transition-colors disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Draft
              </button>
            )}

            {/* Separator */}
            <span className="w-px h-6 bg-[var(--hover-tint)]" />

            {/* Export icon buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleExport('markdown')}
                className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
                title="Export as Markdown"
              >
                <FileCode className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleExport('word')}
                className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
                title="Export as Word"
              >
                <FileText className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
                title="Export as PDF"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={handleCopy}
                className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="h-4 w-4 text-primary-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

        </div>{/* end form sections */}
        </div>{/* end left column */}

        {/* ── Right: strategy + validation + instructions ──────── */}
        <div className="w-72 flex-shrink-0 sticky top-6 space-y-4 self-start">

          {/* Strategy link */}
          <div className="border border-primary-200 dark:border-primary-800">
            <div className="flex items-center gap-2 px-4 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800">
              <TreeDeciduous className="h-3.5 w-3.5 text-primary-500" />
              <span className="text-xs font-semibold text-[var(--text-sec)]">Names opportunity space for</span>
            </div>
            <div className="px-4 py-3 bg-[var(--bg-raised)] overflow-visible">
              <p className="text-xs text-[var(--text-ter)] mb-2">
                Which Strategy does this driver name?
              </p>
              <AppSelect
                value={strategyId}
                onChange={setStrategyId}
                placeholder="— No strategy —"
                accentColor="primary"
                options={strategies.map((s) => ({ value: s.id, label: s.name }))}
              />
            </div>
          </div>

          {/* Validation results (shown after Review) */}
          {showValidation && validationResults && (
            <div className="border border-[var(--mustard)] dark:border-[var(--mustard)] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-b border-[var(--mustard)] dark:border-[var(--mustard)]">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)]" />
                  <span className="text-xs font-semibold text-[var(--text-sec)]">Review Results</span>
                </div>
                <button onClick={() => setShowValidation(false)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="px-4 py-3 bg-[var(--bg-raised)] space-y-3 max-h-80 overflow-y-auto">
                {validationResults.diagnostics.length === 0 && validationResults.suggestions.length === 0 && !validationResults.classificationIssues ? (
                  <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" /> Well-structured driver!
                  </p>
                ) : (
                  <>
                    {validationResults.diagnostics.map((d: string, i: number) => (
                      <p key={i} className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] flex items-start gap-1.5">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />{d}
                      </p>
                    ))}
                    {validationResults.suggestions.map((s: string, i: number) => (
                      <p key={i} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                        <ArrowRight className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary-500" />{s}
                      </p>
                    ))}
                    <button
                      onClick={() => { setClassifierInitialText(''); setShowClassifier(true); }}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" /> Use Sentence Classifier
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Instructions — always visible in panel */}
          <div className="max-h-[32rem] overflow-y-auto">
            <InstructionPanel showJedi={false} showChecklist={false} />
          </div>

        </div>{/* end right panel */}

      </div>{/* end two-column layout */}

      {/* Sentence Classifier Modal */}
      <SentenceClassifier
        isOpen={showClassifier}
        onClose={() => {
          setShowClassifier(false);
          setClassifierInitialText('');
        }}
        initialSentence={classifierInitialText}
        onInsert={handleInsertFromClassifier}
      />

      {/* Examples Modal */}
      {showExamples.bucket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60 p-4">
          <div className="bg-[var(--bg-raised)] shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-[var(--rule)]">
            <div className="sticky top-0 bg-[var(--bg-raised)] border-b border-[var(--rule)] p-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[var(--text-pri)]">
                Examples: {showExamples.bucket === 'current-state' ? 'Current State' : 
                          showExamples.bucket === 'problem-opportunity' ? 'Problem / Opportunity' : 
                          'Ramifications'}
              </h3>
              <button
                type="button"
                onClick={() => setShowExamples({ bucket: null })}
                className="text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)]"
                aria-label="Close examples"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {getExamplesForBucket(showExamples.bucket).map((example, idx) => (
                <div
                  key={idx}
                  className="p-4 border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg-raised)]/60"
                >
                  <p className="text-[var(--text-pri)] mb-2 font-medium">"{example.text}"</p>
                  {example.explanation && (
                    <p className="text-sm text-[var(--text-sec)] italic">{example.explanation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


